import { useGsCloudBackend } from '@gat-ship/http-client'
import { globalUtils } from '@gat-ship/utils'
import type { ISODateTimeString } from '@gat-ship/types'
import type { EtaPrecision } from '../model/eta-bridge'

/**
 * Sentinel value the create form stores in `vesselImo` when the vessel is "to
 * be named". The api-call maps it to `isToBeNamedVessel: true` and omits the
 * IMO from the request body.
 */
export const VESSEL_TBN = 'TBN'

/**
 * Form-facing payload produced by `CreatePortcallForm.getValues()`. `callType`
 * is the port-call-type id and `appointingPrincipal` is the principal client id;
 * both are mapped to their wire field names in {@link createPortcallRequest}.
 */
export interface CreatePortcallPayload {
  agencyId: string
  /** Vessel IMO number, or {@link VESSEL_TBN} for a to-be-named vessel. */
  vesselImo: string
  callType: string
  /**
   * Port-of-call location code, as returned by
   * `ref-data/organization/agencies/ports` (`AgencyServedPortRef.locationCode`)
   * and consumed verbatim by the real `portOfCallLocationCode` body field — the
   * same port identifier, no reformatting between the two endpoints.
   */
  portOfCallLocationCode: string
  appointingPrincipal: string
  /** UTC instant for arrival at the port of call; `null` when not provided. */
  etaAtPort: ISODateTimeString | null
  etaAtPortPrecision: EtaPrecision | null
  /**
   * UTC instant for arrival at the pilot station. When the port does not
   * capture a separate pilot-station ETA, this mirrors `etaAtPort`.
   */
  etaAtPilotStation: ISODateTimeString | null
  etaAtPilotStationPrecision: EtaPrecision | null
}

/**
 * Request body for `POST /port-calls`
 * (`PortCalls.V1.CreatePortCallRequestBody`). `portCallId` is a client-supplied
 * identifier for the new port call.
 */
interface CreatePortCallRequestBody {
  portCallId: string
  portCallTypeId: string
  agencyId: string
  portOfCallLocationCode: string
  principalClientId: string
  isToBeNamedVessel: boolean
  vesselImoNumber: string | null
  etaAtPort: ISODateTimeString | null
  etaAtPortPrecision: EtaPrecision | null
  etaAtPilotStation: ISODateTimeString | null
  etaAtPilotStationPrecision: EtaPrecision | null
}

/**
 * The slice of the created port call the create flow reads back. The backend
 * returns the full `PortCalls.V1.PortCall`; we only need the id and the
 * server-assigned call number (nullable until the backend allocates one).
 */
export interface CreatedPortcall {
  id: string
  callNumber: string | null
}

/** Response for `POST /port-calls` (`PortCalls.V1.CreatePortCallResponse`). */
interface CreatePortCallResponse {
  portCall: CreatedPortcall
}

export function usePortcallApiCalls() {
  const { apiPost } = useGsCloudBackend()

  async function createPortcallRequest(payload: CreatePortcallPayload) {
    const isToBeNamedVessel = payload.vesselImo === VESSEL_TBN
    const body: CreatePortCallRequestBody = {
      portCallId: globalUtils.functions.uuid(),
      portCallTypeId: payload.callType,
      agencyId: payload.agencyId,
      portOfCallLocationCode: payload.portOfCallLocationCode,
      principalClientId: payload.appointingPrincipal,
      isToBeNamedVessel,
      vesselImoNumber: isToBeNamedVessel ? null : payload.vesselImo,
      etaAtPort: payload.etaAtPort,
      etaAtPortPrecision: payload.etaAtPortPrecision,
      etaAtPilotStation: payload.etaAtPilotStation,
      etaAtPilotStationPrecision: payload.etaAtPilotStationPrecision
    }

    const response = await apiPost<
      CreatePortCallResponse,
      CreatePortCallRequestBody
    >('port-calls', body, globalUtils.functions.uuid())
    return response.portCall
  }

  return { createPortcallRequest }
}
